export default function LanguagePolicy() {
  return (
    <div className='legal-doc-body'>

      <p>
        This Language Policy explains how MJMarry manages multilingual content, translations, and user language preferences 
        across our platform and related services. Our goal is to create an inclusive, respectful, and transparent experience 
        for users from diverse linguistic backgrounds, while ensuring that all legal and religious information remains accurate, 
        trustworthy, and faithful to its original meaning.
      </p>

      <div className='privacy terms cookies'>

        <h1>1. Purpose of the Language Policy</h1>
        <p>
          MJMarry serves a global Muslim audience. To support accessibility and understanding, our platform provides content 
          in multiple languages, primarily English and Arabic. This policy outlines how translations are managed, prioritized, 
          and maintained to ensure clarity, consistency, and respect for the Islamic values at the heart of our service.
        </p>
        <p>
          We recognize that language is more than communication — it reflects culture, faith, and identity. Therefore, our 
          multilingual approach is not merely functional but a commitment to inclusivity and dignity for all users.
        </p>

        <h1>2. Official Language of Reference</h1>
        <p>
          The official language of reference for MJMarry’s Terms, Privacy Policy, and User Agreements is <strong>English</strong>. 
          While translations are provided for user convenience, in the event of any inconsistency between the English version 
          and a translated version, the English version shall prevail. This ensures consistent legal interpretation and avoids 
          ambiguity across jurisdictions.
        </p>
        <p>
          We encourage users who rely on translations to refer to the English version for any legally binding interpretation 
          or clarification.
        </p>

        <h1>3. Translations and Accuracy</h1>
        <p>
          We strive to ensure that all translations provided on our platform are accurate, culturally appropriate, and respectful 
          of the nuances inherent in religious and interpersonal communication. Our translation process involves native speakers, 
          contextual review, and cultural sensitivity checks to preserve the intended meaning of every term.
        </p>

        <h2>3.1 Human and AI-Assisted Translation</h2>
        <p>
          MJMarry employs a balanced translation approach. We utilize both professional human translators and AI-assisted 
          technologies to maintain linguistic quality while improving speed and scalability. All official legal or religious 
          materials, however, are manually reviewed by qualified professionals to ensure faithfulness and precision.
        </p>

        <h2>3.2 Religious and Cultural Sensitivity</h2>
        <p>
          Language carries spiritual weight within Islamic communication. Quranic verses, Hadith references, and faith-based 
          expressions (such as “Insha’Allah” or “Alhamdulillah”) are translated or transliterated carefully to preserve meaning 
          and reverence. Any interpretation that could alter theological understanding is avoided.
        </p>

        <h2>3.3 Regional Dialects and Variations</h2>
        <p>
          The Arabic language, like many others, includes diverse dialects. MJMarry primarily uses <strong>Modern Standard Arabic (MSA)</strong> 
          for written materials to maintain neutrality and comprehension across Arabic-speaking regions.
        </p>

        <h1>4. User Language Preferences</h1>
        <p>
          MJMarry allows users to select their preferred language for browsing, communication, and notifications. This preference 
          applies to website content, email updates, system alerts, and in-app messaging where available.
        </p>

        <h2>4.1 Automatic Language Detection</h2>
        <p>
          In some cases, our system detects a user’s default language using browser or device settings to improve usability. 
          Users retain full control and can modify their preferred language anytime within their profile settings.
        </p>

        <h2>4.2 Multilingual Notifications and Emails</h2>
        <p>
          Certain notifications may still appear in English if localized versions are unavailable. However, our team continually 
          expands translation coverage to improve accessibility for all communities.
        </p>

        <h1>5. Communication Between Users</h1>
        <p>
          MJMarry encourages courteous, clear, and culturally mindful communication across all languages. Users should strive 
          to express themselves respectfully and avoid using automated translation tools that may distort meaning in sensitive 
          discussions. 
        </p>
        <p>
          While we promote cross-language interaction, MJMarry is not responsible for misunderstandings that may result from 
          inaccurate translations in user-generated messages.
        </p>

        <h1>6. Legal Documents and Translations</h1>
        <p>
          The following documents are maintained in English as the authoritative legal versions:
        </p>
        <ul>
          <li>Terms and Conditions</li>
          <li>Privacy Policy</li>
          <li>Cookie Policy</li>
          <li>Community Standards & Ethics (CSAE)</li>
          <li>Language Policy</li>
        </ul>
        <p>
          Translated copies serve informational purposes only. If any contradictions arise, the English originals shall 
          govern and supersede all other versions.
        </p>

        <h1>7. Updates to Translations</h1>
        <p>
          Translations are regularly reviewed to align with evolving services, design changes, or new regulatory requirements. 
          MJMarry reserves the right to modify or improve translations without prior notice, ensuring that updated versions 
          reflect both linguistic and cultural correctness.
        </p>

        <h2>7.1 Version Control</h2>
        <p>
          Each major update to our legal or policy translations includes a modification date and may be archived for 
          transparency. Historical versions can be requested by contacting our support team.
        </p>

        <h1>8. Reporting Translation Errors</h1>
        <p>
          We value our community’s input in refining translations. Users are encouraged to report unclear, incorrect, or 
          culturally insensitive translations to <strong>support@mjmarry.com</strong>. Our linguistics and compliance team 
          reviews all feedback promptly and implements corrections as appropriate.
        </p>

        <h1>9. Limitation of Liability</h1>
        <p>
          MJMarry shall not be held responsible for misinterpretations, disputes, or damages arising from translation differences 
          or user-generated multilingual content. Users are advised to refer to English-language versions for legally binding 
          matters or contract interpretation.
        </p>

        <h2>9.1 Third-Party Translations</h2>
        <p>
          Any translations or interpretations provided by third parties (including browser translation extensions) are not 
          endorsed or verified by MJMarry. We cannot guarantee their accuracy or compliance with our standards.
        </p>

        <h1>10. Acknowledgment and Acceptance</h1>
        <p>
          By using MJMarry, you acknowledge and agree that:
        </p>
        <ul>
          <li>English is the governing language for all legal agreements and documents.</li>
          <li>Translations are provided for ease of understanding, not for legal reliance.</li>
          <li>Users bear responsibility for verifying meaning when translation uncertainty arises.</li>
        </ul>

        <h1>11. Contact Information</h1>
        <p>
          For questions, clarifications, or concerns regarding our Language Policy, please contact:
        </p>
        <p>
          <strong>Email:</strong> support@mjmarry.com<br />
        </p>

        <h1>12. Commitment to Continuous Improvement</h1>
        <p>
          MJMarry is continuously improving its multilingual systems to make the platform accessible, fair, and inclusive 
          for Muslims around the world. We believe in communication rooted in <strong>truth, respect, and mutual understanding</strong>, 
          and this Language Policy reflects our dedication to those principles.
        </p>
      </div>
    </div>
  );
}
